import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, RefreshCw, Lock, Unlock, Calculator } from "lucide-react";
import { MgtCentreConfigDialog } from "./MgtCentreConfigDialog";

const fmt = (v) => {
  const n = Number(v || 0);
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export function MgtAllocationSection({
  token,
  month,
  center,      // targetCenter as picked by SA ("ALL" or a specific code)
  mgt,          // salaryData.mgt_allocation payload
  isSuperAdmin,
  canEdit,      // Admin or SA
  onRefresh,    // reload Salary Preview after mutation
}) {
  const [busy, setBusy] = useState(false);

  if (!mgt) return null;

  // Effective-date gate — hidden entirely before July 2026 UNLESS a historical PDF exists
  if (mgt.eligible === false) {
    return null;
  }

  const isHistorical = !!mgt.historical;
  const isAll = (center || "ALL").toUpperCase() === "ALL";
  const isMgt = (center || "").toUpperCase() === "PB-MGT";
  const isIndiaCentre = !isAll && !isMgt && mgt.selected_center_included;
  const isIntl = !isAll && !isMgt && !mgt.selected_center_included;
  const perEmpShare = (e) => {
    if (isHistorical) return Number(e.contribution) || 0;
    return mgt.eligible_center_count
      ? Number(e.contribution) / Number(mgt.eligible_center_count)
      : 0;
  };

  const recalculate = async (state) => {
    if (!canEdit) return;
    setBusy(true);
    try {
      const path = state === "FINAL" ? "/mgt-allocation/finalise" : "/mgt-allocation/save-preview";
      await api.post(path, {
        token, month, reason: state === "FINAL" ? "SA finalised" : "",
      });
      toast.success(state === "FINAL" ? "MGT allocation finalised" : "MGT allocation saved (Preview)");
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save allocation");
    } finally {
      setBusy(false);
    }
  };

  const unlock = async () => {
    const reason = window.prompt("Enter reason for unlocking finalised MGT allocation (min 3 chars):", "");
    if (!reason || reason.trim().length < 3) {
      toast.error("Unlock cancelled — reason (3+ chars) is required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/mgt-allocation/unlock", { token, month, reason: reason.trim() });
      toast.success("MGT allocation unlocked");
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unlock allocation");
    } finally {
      setBusy(false);
    }
  };

  const stateBadge = isHistorical ? (
    <Badge className="bg-amber-100 text-amber-900 border-amber-300"
      data-testid="mgt-allocation-state-badge">
      HISTORICAL MGT PDF — LOCKED
    </Badge>
  ) : mgt.saved_state ? (
    <Badge className={mgt.saved_state === "FINAL"
      ? "bg-green-100 text-green-900 border-green-300"
      : "bg-blue-100 text-blue-900 border-blue-300"}
      data-testid="mgt-allocation-state-badge">
      {mgt.saved_state}
    </Badge>
  ) : (
    <Badge variant="outline" data-testid="mgt-allocation-state-badge">LIVE</Badge>
  );

  const staleBanner = mgt.stale && (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 mb-4"
      data-testid="mgt-allocation-stale-banner">
      <div className="font-semibold mb-1">MGT ALLOCATION IS STALE</div>
      <div>{mgt.stale_reason || "PB-MGT payroll has changed since this allocation was saved."}</div>
      {canEdit && (
        <div className="mt-2 flex gap-2">
          <Button size="sm" variant="secondary" disabled={busy}
            onClick={() => recalculate("PREVIEW")}
            data-testid="mgt-recalculate-preview-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Recalculate (Preview)
          </Button>
          <Button size="sm" disabled={busy}
            onClick={() => recalculate("FINAL")}
            data-testid="mgt-recalculate-finalise-btn">
            <Lock className="w-3.5 h-3.5 mr-1.5" />
            Recalculate &amp; Finalise
          </Button>
        </div>
      )}
    </div>
  );

  // ── SPECIFIC INDIA CENTRE — 3 tiles + centre-scoped MGT breakdown table ──
  if (isIndiaCentre) {
    return (
      <div className="mt-6 border-t pt-6" data-testid="mgt-allocation-section">
        {staleBanner}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-purple-600" />
            <span className="font-semibold text-sm">MGT SALARY ALLOCATION — {month} — {center}</span>
            {stateBadge}
          </div>
          {canEdit && !isHistorical && !mgt.stale && (
            <div className="flex gap-2">
              {isSuperAdmin && (
                <MgtCentreConfigDialog token={token} month={month} onSaved={onRefresh} />
              )}
              {mgt.saved_state !== "FINAL" && (
                <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => recalculate("PREVIEW")}
                  data-testid="mgt-save-preview-btn">
                  Save Preview
                </Button>
              )}
              {mgt.saved_state !== "FINAL" ? (
                <Button size="sm" disabled={busy}
                  onClick={() => recalculate("FINAL")}
                  data-testid="mgt-finalise-btn">
                  <Lock className="w-3.5 h-3.5 mr-1.5" />Finalise
                </Button>
              ) : isSuperAdmin && (
                <Button size="sm" variant="outline" disabled={busy} onClick={unlock}
                  data-testid="mgt-unlock-btn">
                  <Unlock className="w-3.5 h-3.5 mr-1.5" />Unlock
                </Button>
              )}
            </div>
          )}
          {canEdit && !isHistorical && mgt.stale && isSuperAdmin && (
            <MgtCentreConfigDialog token={token} month={month} onSaved={onRefresh} />
          )}
        </div>
        <div className="text-xs text-muted-foreground mb-3">
          {isHistorical
            ? "Recovered from the locked Historical MGT Allocation PDF for this centre. Amounts are stored as-printed and are NOT redivided."
            : "MGT allocation is a cost-attribution layer — added to this centre's Total Manpower Cost but not a separate salary expense."}
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>MGT Employee</TableHead>
              <TableHead>Designation</TableHead>
              <TableHead className="text-right">
                {isHistorical ? "Base Salary" : "PB-MGT Calculated Salary"}
              </TableHead>
              {!isHistorical && <TableHead className="text-right">India Centres</TableHead>}
              <TableHead className="text-right">{center} Share</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(mgt.employees || []).filter((e) => e.included).map((e, i) => {
              const share = perEmpShare(e);
              return (
                <TableRow key={i} data-testid={`mgt-emp-row-${i}`}>
                  <TableCell className="font-medium">{e.employeeName}</TableCell>
                  <TableCell>{e.designation}</TableCell>
                  <TableCell className="text-right">{fmt(e.grossSalary)}</TableCell>
                  {!isHistorical && <TableCell className="text-right">{mgt.eligible_center_count}</TableCell>}
                  <TableCell className="text-right font-semibold text-purple-700">
                    {fmt(share)}
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow className="bg-purple-50 font-bold">
              <TableCell colSpan={isHistorical ? 3 : 4} className="text-right">TOTAL {center} MGT ALLOCATION</TableCell>
              <TableCell className="text-right text-purple-700" data-testid="mgt-centre-total">
                {fmt(mgt.selected_center_amount)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {isHistorical && mgt.invoice_number && (
          <div className="text-[11px] text-muted-foreground mt-2">
            Source: Historical MGT PDF — Invoice {mgt.invoice_number}
            {mgt.invoice_date ? ` · Dated ${mgt.invoice_date}` : ""}
            {mgt.payment_month ? ` · Paid in ${mgt.payment_month}` : ""}
          </div>
        )}
      </div>
    );
  }

  // ── INTERNATIONAL CENTRE (e.g. PB-PERTH) — ₹0 note ──
  if (isIntl) {
    return (
      <div className="mt-6 border-t pt-6" data-testid="mgt-allocation-section-intl">
        <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm">
          <div className="font-semibold mb-1 flex items-center gap-2">
            <Calculator className="w-4 h-4 text-slate-600" />
            MGT SALARY ALLOCATION — {month}
          </div>
          <div>International Centre — Not Eligible for India MGT Allocation</div>
          <div className="font-bold text-lg text-slate-700 mt-1" data-testid="mgt-intl-zero">₹0.00</div>
        </div>
      </div>
    );
  }

  // ── PB-MGT CENTRE VIEW — allocation summary + employee breakdown ──
  if (isMgt) {
    return (
      <div className="mt-6 border-t pt-6" data-testid="mgt-allocation-section-mgt">
        {staleBanner}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-purple-600" />
            <span className="font-semibold text-sm">MGT SALARY ALLOCATION SUMMARY — {month}</span>
            {stateBadge}
          </div>
          {canEdit && (
            <div className="flex gap-2">
              {isSuperAdmin && (
                <MgtCentreConfigDialog token={token} month={month} onSaved={onRefresh} />
              )}
              {mgt.saved_state !== "FINAL" && !mgt.stale && (
                <Button size="sm" disabled={busy}
                  onClick={() => recalculate("FINAL")}
                  data-testid="mgt-finalise-btn">
                  <Lock className="w-3.5 h-3.5 mr-1.5" />Finalise Allocation
                </Button>
              )}
              {mgt.saved_state === "FINAL" && isSuperAdmin && !mgt.stale && (
                <Button size="sm" variant="outline" disabled={busy} onClick={unlock}
                  data-testid="mgt-unlock-btn">
                  <Unlock className="w-3.5 h-3.5 mr-1.5" />Unlock
                </Button>
              )}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <MetricTile label="PB-MGT Salary Total" value={fmt(mgt.mgt_center_total)} testId="mgt-tile-total" />
          <MetricTile label="Excluded from Allocation" value={fmt(mgt.excluded_amount)} testId="mgt-tile-excluded" />
          <MetricTile label="Eligible Allocation Pool" value={fmt(mgt.pool_amount)} testId="mgt-tile-pool" accent="purple" />
          <MetricTile label="Eligible India Centres" value={mgt.eligible_center_count} testId="mgt-tile-count" />
          <MetricTile label="Per India Centre" value={fmt(mgt.per_center_amount)} testId="mgt-tile-per-centre" accent="purple" />
        </div>
        <div className="text-xs text-muted-foreground mb-2">
          Below lists employees included in the MGT Cost Allocation pool.
          Non-included PB-MGT employees remain in the normal PB-MGT payroll
          worksheet above (they still receive salary; their salary is simply
          not distributed to India centres).
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Designation</TableHead>
              <TableHead className="text-right">Calculated Salary</TableHead>
              <TableHead className="text-right">Contribution</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(mgt.employees || []).filter((e) => e.included).map((e, i) => (
              <TableRow key={i} data-testid={`mgt-emp-row-${i}`}>
                <TableCell className="font-medium">{e.employeeName}</TableCell>
                <TableCell>{e.designation}</TableCell>
                <TableCell className="text-right">{fmt(e.grossSalary)}</TableCell>
                <TableCell className="text-right font-semibold text-purple-700">
                  {fmt(e.contribution)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  // ── ALL CENTRES — consolidated PB-MGT summary + centre-wise allocation ──
  return (
    <div className="mt-6 border-t pt-6" data-testid="mgt-allocation-section-all">
      {staleBanner}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-purple-600" />
          <span className="font-semibold text-sm">MGT SALARY SUMMARY — {month}</span>
          {stateBadge}
        </div>
        {canEdit && (
          <div className="flex gap-2">
            {isSuperAdmin && (
              <MgtCentreConfigDialog token={token} month={month} onSaved={onRefresh} />
            )}
            {mgt.saved_state !== "FINAL" && !mgt.stale && (
              <Button size="sm" disabled={busy}
                onClick={() => recalculate("FINAL")}
                data-testid="mgt-finalise-btn">
                <Lock className="w-3.5 h-3.5 mr-1.5" />Finalise Allocation
              </Button>
            )}
            {mgt.saved_state === "FINAL" && isSuperAdmin && !mgt.stale && (
              <Button size="sm" variant="outline" disabled={busy} onClick={unlock}
                data-testid="mgt-unlock-btn">
                <Unlock className="w-3.5 h-3.5 mr-1.5" />Unlock
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="text-xs text-muted-foreground mb-3">
        MGT employees below participate in the India-centre cost allocation.
        Non-included PB-MGT employees are excluded from allocation but their
        salary is <strong>still counted once</strong> in the ALL CENTRES total salary above.
        Allocation is attributed to India centres; PB-MGT payroll is counted only once in company-wide totals.
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>MGT Employee</TableHead>
            <TableHead>Designation</TableHead>
            <TableHead className="text-right">PB-MGT Calculated Salary</TableHead>
            <TableHead className="text-right">Contribution</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(mgt.employees || []).filter((e) => e.included).map((e, i) => (
            <TableRow key={i} data-testid={`mgt-all-emp-row-${i}`}>
              <TableCell className="font-medium">{e.employeeName}</TableCell>
              <TableCell>{e.designation}</TableCell>
              <TableCell className="text-right">{fmt(e.grossSalary)}</TableCell>
              <TableCell className="text-right font-semibold text-purple-700">
                {fmt(e.contribution)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 mb-4">
        <MetricTile label="PB-MGT Salary Total" value={fmt(mgt.mgt_center_total)} testId="mgt-tile-total" />
        <MetricTile label="Excluded from Allocation" value={fmt(mgt.excluded_amount)} testId="mgt-tile-excluded" />
        <MetricTile label="Eligible Allocation Pool" value={fmt(mgt.pool_amount)} testId="mgt-tile-pool" accent="purple" />
        <MetricTile label="Eligible India Centres" value={mgt.eligible_center_count} testId="mgt-tile-count" />
        <MetricTile label="Per India Centre" value={fmt(mgt.per_center_amount)} testId="mgt-tile-per-centre" accent="purple" />
      </div>
      <div className="font-semibold text-sm mb-2">CENTRE-WISE MGT ALLOCATION</div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Centre</TableHead>
            <TableHead className="text-center">Eligible?</TableHead>
            <TableHead className="text-right">MGT Allocation</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.entries(mgt.per_center_map || {}).map(([code, amt]) => (
            <TableRow key={code} data-testid={`mgt-centre-row-${code}`}>
              <TableCell className="font-medium">{code}</TableCell>
              <TableCell className="text-center">
                <Badge className="bg-green-100 text-green-900">YES</Badge>
              </TableCell>
              <TableCell className="text-right font-semibold text-purple-700">{fmt(amt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}


function MetricTile({ label, value, testId, accent }) {
  const cls = accent === "purple"
    ? "bg-purple-50 border-purple-200"
    : "bg-slate-50 border-slate-200";
  return (
    <Card className={cls} data-testid={testId}>
      <CardContent className="pt-4 text-center">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`text-lg font-bold mt-1 ${accent === "purple" ? "text-purple-700" : "text-slate-800"}`}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}
